import React, { useRef, useEffect } from 'react';
import { format } from 'date-fns';
import ThinkingAnimation from './ThinkingAnimation';

// Message type definition
interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

interface MessageListProps {
  messages: Message[];
  isTyping?: boolean;
  isThinking?: boolean;
}

const MessageList: React.FC<MessageListProps> = ({ messages, isTyping = false, isThinking = false }) => {
  const bottomRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping, isThinking]);

  // Use either isTyping or isThinking (backwards compatibility)
  const showAnimation = isTyping || isThinking;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="p-3 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`rounded-lg p-3 max-w-[80%] break-words ${
                message.sender === 'user'
                  ? 'bg-gradient-to-r from-gebeya-pink to-gebeya-orange text-white'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              <div className="text-sm whitespace-pre-wrap">{message.text}</div>
              <div className={`text-xs mt-1 ${message.sender === 'user' ? 'text-white/70' : 'text-gray-500'}`}>
                {format(message.timestamp, 'h:mm a')}
              </div>
            </div>
          </div>
        ))}

        {showAnimation && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-lg p-3 max-w-[80%]">
              <ThinkingAnimation />
            </div>
          </div>
        )}
        
        <div ref={bottomRef} />
      </div>
    </div>
  );
};

export default MessageList; 